import type { MaybeRefOrGetter } from 'vue';
import type { FieldFilter, PrimitiveType } from '#layers/data-model';
import type { UIFilterOperator } from '~/components/Filter/utils/filter-ui-operator';
import { computed, toValue } from 'vue';
import { useI18n } from 'vue-i18n';
import { FilterOperator, getFieldFilterOperatorName, getPrimitiveFieldPresentationDefaults } from '#layers/data-model';

export type SupportedOperatorsSpec =
	| FilterOperator[]
	| ((operators: FilterOperator[]) => FilterOperator[]);

export interface OperatorResolutionOptions {
	mode?: 'default' | 'advanced';
}

export function useFilterOperator(filter: MaybeRefOrGetter<FieldFilter>) {
	const { t } = useI18n();

	const operator = computed(() => getFieldFilterOperatorName(toValue(filter)));
	const label = computed(() => operator.value ? getOperatorLabel(operator.value) : '');

	return {
		label,
		operator,
		getOperatorLabel,
	};

	function getOperatorLabel(operator: UIFilterOperator) {
		return t(`filters.operators.${operator}`);
	}
}

export function getSupportedOperators(type: PrimitiveType): FilterOperator[] {
	return getPrimitiveFieldPresentationDefaults(type).filter?.supportedOperators ?? [];
}

export function getDefaultOperators(type: PrimitiveType): FilterOperator[] {
	const filter = getPrimitiveFieldPresentationDefaults(type).filter;
	return filter?.defaultOperators ?? filter?.supportedOperators ?? [];
}

export function narrowOperators(
	baseOperators: FilterOperator[],
	spec?: SupportedOperatorsSpec,
): FilterOperator[] {
	if (!spec) return baseOperators;
	if (Array.isArray(spec)) return baseOperators.filter((op) => spec.includes(op));
	return spec(baseOperators);
}

export function getNarrowedOperatorsForField(
	field: { type: PrimitiveType } | undefined,
	spec: SupportedOperatorsSpec | undefined,
	options: OperatorResolutionOptions = {},
): FilterOperator[] {
	if (!field) return [];
	const baseOps = options.mode === 'advanced' ? getSupportedOperators(field.type) : getDefaultOperators(field.type);
	return narrowOperators(baseOps, spec);
}

export function getDefaultOperatorForField(
	field: { type: PrimitiveType } | undefined,
	spec: SupportedOperatorsSpec | undefined,
): FilterOperator {
	return getNarrowedOperatorsForField(field, spec)[0] ?? FilterOperator.EQ;
}
